import { ValidatorFn, AbstractControl, ValidationErrors, AsyncValidatorFn } from "@angular/forms";
import { AisProduct } from "../enums/AisProducts";
import { Temporality } from "src/app/update-notice/model/temporality";
import moment from "moment";
import { Observable, of, map, startWith, take } from "rxjs";

export const NOTAM_ITEM_E_PATTERN = /^[A-Z0-9\-?:().,=/+\s]+$/;

export const LOWER_LIMIT_PATTERN = /^(SFC|GND|\d{1,5}(FT|M) (AGL|AMSL)|FL\d{3})$/;

export const UPPER_LIMIT_PATTERN = /^(UNL|\d{1,5}(FT|M) (AGL|AMSL)|FL\d{3})$/;

export const uppercaseLetterValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;
  return /^[A-Z]$/.test(control.value) ? null : { uppercaseLetter: true };
};

export const positiveNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;
  return /^[0-9]\d*$/.test(control.value) ? null : { positiveNumber: true };
};

export const fourDigitYearValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;
  return /^(19|20)\d{2}$/.test(control.value) ? null : { invalidYear: true };
};

export const fourUppercaseLettersValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;
  return control.value.length === 4 ? null : { fourUppercaseLetters: true };
};

export const NOTAM_RC_FIELDS_VALIDATOR_ASYNC: AsyncValidatorFn = (
  control: AbstractControl,
): Observable<ValidationErrors | null> => {
  if (!control.parent) return of(null);
  return control.parent.valueChanges.pipe(
    startWith(control.parent.value),
    map((form) => {
      const aisProduct = form.aisProduct;

      if (aisProduct !== AisProduct.NOTAM) return null;

      const notamType = form.notamType;
      return ["R", "C"].includes(notamType?.[0]) && !control.value ? { required: true } : null;
    }),
    take(1),
  );
};
export const NOTAM_NRC_FIELDS_VALIDATOR_ASYNC: AsyncValidatorFn = (
  control: AbstractControl,
): Observable<ValidationErrors | null> => {
  if (!control.parent) return of(null);
  return control.parent.valueChanges.pipe(
    startWith(control.parent.value),
    map((form) => {
      const aisProduct = form.aisProduct;

      if (aisProduct !== AisProduct.NOTAM) return null;

      const notamType = form.notamType;

      return ["N", "R", "C"].includes(notamType?.[0]) && !control.value ? { required: true } : null;
    }),
    take(1),
  );
};

export const CANCEL_DATE_VALIDATOR_ASYNC: AsyncValidatorFn = (
  control: AbstractControl,
): Observable<ValidationErrors | null> => {
  if (!control.parent) return of(null);
  return control.parent.valueChanges.pipe(
    startWith(control.parent.getRawValue()),
    map((form) => {
      if (!form.temporality?.length) return null;
      const aisProduct = form.aisProduct;
      const isTemporary = form.temporality[0] === Temporality.TEMPORARY;
      const effectiveDate: moment.Moment = form.effectiveDate;
      const cancelDate: moment.Moment = form.cancelDate;
      const shouldRequired = !cancelDate && aisProduct === AisProduct.NOTAM;
      if (!isTemporary) return null;

      return shouldRequired
        ? { required: true }
        : cancelDate?.isBefore(effectiveDate, "day")
          ? { invalidCancelDate: true }
          : null;
    }),
    take(1),
  );
};

export const verticalLimitsValidator = (fieldType: "lower" | "upper"): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = control.value?.toUpperCase().trim();
    if (!val) return null;

    // 1. Constants & Flight Level
    const allowedConstants = fieldType === "lower" ? ["SFC", "GND"] : ["UNL"];
    if (allowedConstants.includes(val)) return null;
    if (val.startsWith("F")) {
      return /^FL\d{3}$/.test(val) ? null : { errorType: "FL_FORMAT" };
    }

    // 2. Altitude Regex
    // Group 1: Digits
    // Group 2: The Unit (Immediately after digits)
    // Group 3: Everything else (The Reference part)
    const altitudeRegex = /^(\d+)([A-Z]+)?(?:\s+(.*))?$/;
    const match = val.match(altitudeRegex);

    if (match) {
      const [, digits, capturedUnit, capturedRef] = match;

      if (digits.length > 5) return { errorType: "INVALID_DIGITS" };
      
      // A. Validate Unit
      if (!capturedUnit) return { errorType: "MISSING_UNIT" };
      if (!["FT", "M"].includes(capturedUnit)) return { errorType: "INVALID_UNIT" };

      // B. Validate Reference
      if (!capturedRef) return { errorType: "MISSING_REF" };

      // Now "A A" or "ACL" will hit this check instead of failing the regex
      if (!["AGL", "AMSL"].includes(capturedRef)) return { errorType: "WRONG_REF" };

      return null; // Valid
    }

    // 3. Final Fallback (For non-numeric strings that aren't constants)
    return { errorType: "INVALID_REF", allowed: allowedConstants.join("/") };
  };
};
